/**
 * Browser ⇄ server WebSocket protocol at `/ws`. Auth is the session cookie.
 *
 * One socket carries every home the user can access. Flow:
 *   server → welcome
 *   server → snapshot (one per accessible home)
 *   server → diff* / model / access / home_status / boosts as things change
 *   client → call / history / logbook / stats / ping / resync
 *
 * Close codes: 4401 unauthenticated, 4403 access revoked (user disabled / invite ended).
 */
import { z } from 'zod';
import { AccessSchedule, ResolvedAccess } from './access.js';
import { Boost, EntityState, HomeModel, HomeSummary } from './model.js';
import { ClimateActionMsg } from './heating.js';
export const PublicUser = z.object({
    id: z.string(),
    username: z.string(),
    displayName: z.string(),
    isOwner: z.boolean(),
    mustChangePassword: z.boolean(),
    isGuest: z.boolean(),
});
// ---------- client → server ----------
export const CallMsg = z.object({
    t: z.literal('call'),
    id: z.number(),
    home: z.string(),
    domain: z.string(),
    service: z.string(),
    /** Always explicit entity ids. Area/device targets are rejected. */
    entityIds: z.array(z.string()).min(1),
    data: z.record(z.string(), z.unknown()).optional(),
});
export const HistoryMsg = z.object({
    t: z.literal('history'),
    id: z.number(),
    home: z.string(),
    entityIds: z.array(z.string()).min(1).max(12),
    start: z.number(), // epoch seconds
    end: z.number().optional(),
    /** Use long-term statistics (5-min / hour) instead of raw states. */
    statistics: z.enum(['5minute', 'hour', 'day']).optional(),
});
export const LogbookMsg = z.object({
    t: z.literal('logbook'),
    id: z.number(),
    home: z.string(),
    entityId: z.string(),
    start: z.number(),
    end: z.number().optional(),
});
export const BoostMsg = z.object({
    t: z.literal('boost'),
    id: z.number(),
    home: z.string(),
    systemId: z.string(),
    action: z.enum(['start', 'cancel']),
});
export const ClientMsg = z.discriminatedUnion('t', [
    CallMsg,
    HistoryMsg,
    LogbookMsg,
    BoostMsg,
    ClimateActionMsg,
    z.object({ t: z.literal('ping') }),
    z.object({ t: z.literal('resync'), home: z.string().optional() }),
]);
export const CLOSE_UNAUTHENTICATED = 4401;
export const CLOSE_REVOKED = 4403;
//# sourceMappingURL=protocol.js.map